import classNames from 'classnames';
import React, { useMemo } from 'react';

import { IconTypes } from '@eika/icon';
import { IconLink, Link } from '@eika/link';

import { WispSignal } from '../../components/WispSignal';
import { useConfig } from '../../defaultConfig';
import { useMultipleAgreements } from '../../fetchHooks/useProfile';
import useUnreadMessages from '../../fetchHooks/useUnreadMessages';
import useTranslation, { format } from '../../hooks/useTranslation';
import TranslationsConfigType from '../../translations/translationType';

import './ProfileHeaderLink.scss';

const formatUnreadMessagesString = (translated: TranslationsConfigType, numUnreadMessages: number) => {
    if (numUnreadMessages === 1) {
        return `${translated.profile.myProfile}, ${format(translated.profile.unreadMessagesSingle, numUnreadMessages)}`;
    }
    if (numUnreadMessages >= 2) {
        return `${translated.profile.myProfile}, ${format(translated.profile.unreadMessagesplural, numUnreadMessages)}`;
    }

    return translated.profile.myProfile;
};

const getUnreadMessagesNumber = (numUnreadMessages: number) => {
    if (numUnreadMessages > 9) {
        return '9+';
    }
    return numUnreadMessages > 0 ? numUnreadMessages : '';
};

const CompactProfileLink = ({ totalUnreadMessages, url }: { totalUnreadMessages: number; url: string }) => {
    const translated = useTranslation();
    return (
        <>
            <IconLink className="header__profile-button native" icon={IconTypes.FRAMED_PROFILE_24} href={url}>
                <span className="screen-reader-text">
                    {formatUnreadMessagesString(translated, totalUnreadMessages)}
                </span>
                {totalUnreadMessages > 0 && (
                    <span className="header__profile-button-counter">
                        {getUnreadMessagesNumber(totalUnreadMessages)}
                    </span>
                )}
            </IconLink>
        </>
    );
};

const ProfileHeaderLink: React.FC = () => {
    const { isNative, frontpage, profileLink, cibLink: cibProfileLink } = useConfig();
    const { profile } = useTranslation();

    const enabled = (isNative && frontpage && profileLink) || (!isNative && profileLink);

    const { data: hasMultipleAgreements } = useMultipleAgreements();
    const { data: unreadMessages } = useUnreadMessages(Boolean(enabled));

    const cibLink = useMemo(() => {
        const fullHostName = window.location.hostname;
        const wwwIndex = fullHostName.indexOf('www.');
        if (wwwIndex < 0) return `https://nettbedrift.${fullHostName}`;
        const hostName = fullHostName.slice(wwwIndex + 4);
        return `https://nettbedrift.${hostName}`;
    }, []);

    if ((isNative && !frontpage) || !profileLink) {
        return null;
    }

    const totalUnreadMessages =
        (unreadMessages?.numberOfUnreadLetters ?? 0) + (unreadMessages?.numberOfUnreadMessages ?? 0);

    if (frontpage && isNative) {
        return (
            <div className="header__container">
                <div className="header__wisp-signal">
                    <WispSignal />
                </div>
                <CompactProfileLink totalUnreadMessages={totalUnreadMessages} url={profileLink.url} />
            </div>
        );
    }
    const screenReaderText =
        totalUnreadMessages > 1
            ? format(profile.unreadMessagesplural, totalUnreadMessages)
            : format(profile.unreadMessagesSingle, totalUnreadMessages);
    return (
        <div className="header__container">
            <div className="header__wisp-signal">
                <WispSignal />
            </div>
            <CompactProfileLink totalUnreadMessages={totalUnreadMessages} url={profileLink.url} />
            <div className="header__profile-links">
                <Link
                    className="header__profile-button desktop"
                    icon={IconTypes.FRAMED_PROFILE_22}
                    href={profileLink.url}
                >
                    {profile.myProfile}
                    {totalUnreadMessages > 0 && (
                        <span
                            className={classNames('header__profile-button-counter', {
                                'more-than-nine': totalUnreadMessages > 9,
                            })}
                        >
                            <span className="screen-reader-text">{screenReaderText}</span>
                        </span>
                    )}
                </Link>
                {hasMultipleAgreements?.hasAgreements && (
                    <Link
                        className="header__profile-button desktop"
                        icon={IconTypes.OVERFOERE_24}
                        href={cibProfileLink?.url ?? cibLink}
                    >
                        {profile.switchAgreement}
                    </Link>
                )}
            </div>
        </div>
    );
};

export default ProfileHeaderLink;
